import { useState } from 'react'
import type { JSX } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { formatDateTime, formatDuration, formatTime } from '@/lib/format'
import { useActiveTenant } from '@/tenant/context'
import { Empty, ErrorState, Loading } from '@/ui/states'

type RunSummary = Schema<'RunSummaryOut'>
type RunDetail = Schema<'RunDetailOut'>
type Step = Schema<'ReasoningStepOut'>
type Stage = Schema<'PassStageOut'>

/**
 * The reasoning trail — S-D5's screen 10, on S-B29's persisted runs (S-B32c).
 *
 * The design's own note is the requirement: *"every run writes the full chain —
 * what it observed, what it concluded, what it did, and what it cost. This is
 * what makes an autonomous agent auditable rather than a black box, and it is
 * the first thing anyone opens when they disagree with an outcome."* So this
 * screen has one job, which is to show a chain in order and hide nothing.
 *
 * **Every run, not every pass.** `GET /seo/runs` was widened in this session
 * (`app/db/runs.history`): a tenant who presses *Run audit now* gets a root run
 * that writes the same audit → decision → action chain a pass's audit stage
 * writes, and a picker that offered only passes would call itself *the*
 * reasoning trail while hiding the reasoning behind every button in the module.
 *
 * **Two lines per step, and the server decides the split.** `title` and `body`
 * arrive already separated off `app/seo/trail.py`, because the convention they
 * are separated on — `label: sentence` — is stated in three modules under
 * `app/seo/` and a fourth copy of it here would be the one that disagreed.
 *
 * **The detail is disclosed, not hidden.** Each step carries the structured
 * evidence for its own sentence, and that is the thing a tenant who disagrees
 * with an outcome actually needs. It sits behind a `<details>` because a
 * fifteen-key object above the next step would push the chain off the screen —
 * the same reason the export exists.
 *
 * **The export is the response, not a rendering of it.** `Export JSON` writes
 * out what the endpoint returned — including each step's stored `summary`,
 * unsplit — so what leaves the product is the row rather than this screen's
 * reading of it.
 *
 * **No rupee badge**, where the prototype shows *₹18.40*. Nothing joins a run
 * to `usage_metering`: it is keyed by tenant, period and metric, and a pass's
 * stages run in-process rather than through the queue so most carry no `job_id`
 * either. The badge therefore states the tokens and the models, which is a
 * figure that reconciles against the rows, and the money stays on the budget
 * burn panel where `app/v1/spend.py` derives it from the ledger that holds it.
 * This is the same call `RunTriggers.tsx` made about the prototype's cap: a
 * number that is not derived from anything is worse than no number.
 */
export function ReasoningTrail(): JSX.Element {
  // S-G6. `?run=` is how the approval queue's *See the full reasoning trail →*
  // link arrives — §V2. It is the **initial** selection and not a controlled
  // one: the picker below still sets `runId`, which wins, so following the link
  // and then choosing another run does not fight the URL. A run that is not in
  // the recent list falls back to the newest and says so, rather than silently
  // showing a different article's chain under a link that promised this one's.
  const [params] = useSearchParams()
  const asked = params.get('run')
  const [siteId, setSiteId] = useState<string | null>(null)
  const [runId, setRunId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const selectedSite = siteId ?? listed[0]?.id ?? null

  const runs = useQuery(
    (signal) =>
      selectedSite === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/runs', { params: { query: { site_id: selectedSite } }, signal }),
          ),
    [selectedSite],
  )

  // The newest run until somebody picks another, derived rather than set from an
  // effect — `SearchPerformance`'s reason: an effect would render once with no
  // selection and once with one, and fetch the detail on the second.
  const available = runs.state.data?.items ?? []
  const wanted = runId ?? asked
  const chosen = available.find((run) => run.id === wanted) ?? available[0] ?? null
  const missed = wanted !== null && chosen !== null && chosen.id !== wanted

  const detail = useQuery(
    (signal) =>
      chosen === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/runs/{run_id}', {
              params: { path: { run_id: chosen.id } },
              signal,
            }),
          ),
    [chosen?.id],
  )

  return (
    <div className="ar-stack ar-stack--loose">
      <div className="ar-row" style={{ justifyContent: 'space-between' }}>
        <h1 className="ar-h1">Reasoning trail</h1>
        <div className="ar-btn-group">
          {listed.length > 1 ? (
            <select
              className="ar-select"
              style={{ height: 'var(--ar-control-sm)', width: 'auto' }}
              aria-label="Site"
              value={selectedSite ?? ''}
              onChange={(event) => {
                setSiteId(event.target.value)
                // The run belongs to the old site. Keeping it would leave the
                // picker showing this site's runs and the card showing another
                // site's chain, which is the one thing this screen cannot do.
                setRunId(null)
              }}
            >
              {listed.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.domain}
                </option>
              ))}
            </select>
          ) : null}
          {available.length > 0 && chosen !== null ? (
            <RunPicker
              runs={available}
              chosen={chosen}
              onChoose={(id) => {
                setRunId(id)
              }}
            />
          ) : null}
          {detail.state.status === 'ready' && detail.state.data !== null ? (
            <ExportButton run={detail.state.data} />
          ) : null}
        </div>
      </div>

      {sites.state.status === 'loading' ? <Loading label="Loading your sites" lines={3} /> : null}

      {sites.state.status === 'error' ? (
        <div className="ar-card">
          <ErrorState
            failure={sites.state.failure}
            title="Could not load your sites"
            onRetry={sites.refetch}
          />
        </div>
      ) : null}

      {sites.state.status === 'ready' && listed.length === 0 ? (
        <div className="ar-card">
          <Empty
            icon="🌐"
            title="Add a website to get started"
            body="AugRank writes down what it observed, what it concluded and what it did on every run. Point it at a site and the first run's reasoning appears here."
            action={
              <Link className="ar-btn ar-btn--primary" to="/onboarding">
                Add a site
              </Link>
            }
          />
        </div>
      ) : null}

      {missed ? (
        <div className="ar-alert ar-alert--warning" role="status">
          <span className="ar-alert__icon" aria-hidden="true">
            ⚠
          </span>
          <div className="ar-alert__body">
            <strong>That run is not in this site&apos;s recent list</strong>
            The link asked for a run this site has not kept in view — it may belong to another site,
            or be older than the last thirty. The newest run is shown instead.
          </div>
        </div>
      ) : null}

      {selectedSite !== null ? <RunView runs={runs} detail={detail} chosen={chosen} /> : null}
    </div>
  )
}

type RunsQuery = ReturnType<typeof useQuery<Schema<'RunListOut'> | null>>
type DetailQuery = ReturnType<typeof useQuery<RunDetail | null>>

/**
 * The card, or the reason there is not one.
 *
 * The two queries fail apart: a picker that loaded and a detail that did not is
 * a run whose chain could not be read, and it says so beside a picker the
 * tenant can use to try another run — which beats blanking the screen.
 */
function RunView({
  runs,
  detail,
  chosen,
}: {
  runs: RunsQuery
  detail: DetailQuery
  chosen: RunSummary | null
}): JSX.Element | null {
  if (runs.state.status === 'loading') return <Loading label="Loading this site's runs" lines={4} />

  if (runs.state.status === 'error') {
    return (
      <div className="ar-card">
        <ErrorState
          failure={runs.state.failure}
          title="Could not load this site's runs"
          onRetry={runs.refetch}
        />
      </div>
    )
  }

  if (chosen === null) {
    return (
      <div className="ar-card">
        <Empty
          icon="🧭"
          title="Nothing has run on this site yet"
          body="Every audit, keyword check and article AugRank works on writes down what it observed, what it concluded and what it did. Run one, or set a schedule, and the first chain appears here."
          action={
            <Link className="ar-btn ar-btn--primary" to="/seo/triggers">
              Set up when we run
            </Link>
          }
        />
      </div>
    )
  }

  if (detail.state.status === 'loading') {
    return <Loading label="Loading this run's reasoning" lines={6} />
  }

  if (detail.state.status === 'error') {
    return (
      <div className="ar-card">
        <ErrorState
          failure={detail.state.failure}
          title="Could not load this run's reasoning"
          onRetry={detail.refetch}
        />
      </div>
    )
  }

  return detail.state.data === null ? null : <RunCard run={detail.state.data} />
}

/**
 * What `GET /seo/runs` will return at most, from `openapi.json`, where `default`
 * and `maximum` are both 50 — the cap in force even though the call sends no
 * `limit` (WEB-16).
 */
const RUN_LIMIT = 50

/** The picker. One `<option>` per run, named by what it was and when it ran. */
function RunPicker({
  runs,
  chosen,
  onChoose,
}: {
  runs: RunSummary[]
  chosen: RunSummary
  onChoose: (id: string) => void
}): JSX.Element {
  const tenant = useActiveTenant().tenant
  return (
    <select
      className="ar-select"
      style={{ height: 'var(--ar-control-sm)', width: 'auto' }}
      aria-label="Run"
      value={chosen.id}
      onChange={(event) => {
        onChoose(event.target.value)
      }}
    >
      {runs.map((run) => (
        <option key={run.id} value={run.id}>
          {run.label} · {formatDateTime(run.created_at, tenant.timezone)}
        </option>
      ))}
      {/* WEB-16. The route caps the list and offers no cursor, so a site with
          more than `RUN_LIMIT` runs loses the older ones with no sign of it.
          A trailing disabled option rather than a sentence beside the control:
          this is read at the moment somebody has scrolled to the bottom looking
          for a run that is not there, which is the only moment it matters. */}
      {runs.length >= RUN_LIMIT ? (
        <option disabled>Showing the {RUN_LIMIT} most recent runs</option>
      ) : null}
    </select>
  )
}

/**
 * Export JSON — the response as it arrived, saved to a file.
 *
 * A blob and an object URL rather than a route: the payload is already in the
 * page, a second endpoint would be a second serialisation of the same rows, and
 * this way what is exported is provably what was rendered. The URL is revoked
 * straight after the click, because an object URL lives as long as the document
 * and this button can be pressed once per run for as long as the tab is open.
 */
function ExportButton({ run }: { run: RunDetail }): JSX.Element {
  return (
    <button
      type="button"
      className="ar-btn ar-btn--ghost ar-btn--sm"
      onClick={() => {
        const blob = new Blob([JSON.stringify(run, null, 2)], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        // The short id, which is what the card's own heading shows — a filename
        // carrying a full UUID is one nobody can tell apart at a glance in a
        // downloads folder, which is where these end up.
        link.download = `augrank-run-${run.id.slice(0, 8)}.json`
        link.click()
        URL.revokeObjectURL(url)
      }}
    >
      Export JSON
    </button>
  )
}

/** What each run status is called, and how the badge is coloured. */
const STATUS_TONE: Record<string, { tone: string; label: string }> = {
  queued: { tone: 'ar-badge--neutral', label: 'Queued' },
  running: { tone: 'ar-badge--info', label: 'Running' },
  succeeded: { tone: 'ar-badge--success', label: '✓ Completed' },
  partial: { tone: 'ar-badge--warning', label: '⚠ Partly done' },
  // S-B30. Neither failed nor partial: it finished, and a spend ceiling is why
  // it did less than it was asked for. `0028_run_degraded` argues the split, and
  // the two point a tenant at different screens.
  degraded: { tone: 'ar-badge--warning', label: '⚠ Stopped on budget' },
  failed: { tone: 'ar-badge--danger', label: '✗ Failed' },
  cancelled: { tone: 'ar-badge--neutral', label: 'Stopped' },
  timed_out: { tone: 'ar-badge--danger', label: '✗ Timed out' },
}

/**
 * One run: what it was, how it went, what it cost, and its chain.
 *
 * The heading carries the **short id**, as the prototype does, because that is
 * what a tenant quotes to support and what the exported filename carries.
 */
function RunCard({ run }: { run: RunDetail }): JSX.Element {
  const tenant = useActiveTenant().tenant
  const status = STATUS_TONE[run.status] ?? { tone: 'ar-badge--neutral', label: run.status }
  // Only a finished run has a duration, and `started_at` is null on one that was
  // cancelled before its task ever began — `app/db/runs.open_pass` opens a pass
  // as `queued` precisely so that "never started" stays distinguishable.
  const took =
    run.started_at !== null && run.finished_at !== null
      ? formatDuration(run.started_at, run.finished_at)
      : null

  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>
          {run.label} {run.id.slice(0, 8)} — {formatDateTime(run.created_at, tenant.timezone)}
        </h3>
        <span className="ar-card__spacer" />
        <span className={`ar-badge ${status.tone}`}>
          {status.label}
          {took === null ? '' : ` in ${took}`}
        </span>
        {run.usage.metered ? (
          <span className="ar-badge ar-badge--neutral">
            {(run.usage.input_tokens + run.usage.output_tokens).toLocaleString('en-IN')} tokens
            {run.usage.models.length > 0 ? ` · ${run.usage.models.join(', ')}` : ''}
          </span>
        ) : null}
      </div>
      <div className="ar-card__body ar-stack">
        {run.error !== null ? (
          <div className="ar-alert ar-alert--danger" role="status">
            <span className="ar-alert__icon" aria-hidden="true">
              !
            </span>
            <div className="ar-alert__body">
              <strong>This run did not finish its work</strong>
              {run.error}
            </div>
          </div>
        ) : null}

        {run.stages.length > 0 ? <Stages stages={run.stages} /> : null}

        {run.trail.length === 0 ? (
          <Empty
            icon="⏳"
            title={
              run.finished_at === null
                ? 'This run has not written anything yet'
                : 'This run wrote no reasoning'
            }
            body={
              run.finished_at === null
                ? 'The chain fills in as the run works. Reload in a moment to see what it has observed so far.'
                : 'It ended before it observed anything — the run card above says how. Nothing was changed on your site.'
            }
          />
        ) : (
          <div className="app-trail">
            {run.trail.map((step, index) => (
              <TrailStep
                key={`${step.run_id}:${String(step.step_number)}`}
                step={step}
                timeZone={tenant.timezone}
                last={index === run.trail.length - 1}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * A pass's stages, above its chain.
 *
 * Only a pass has any: a standalone run *is* the work, and the server returns an
 * empty list for one rather than a single stage describing itself. Drawn as
 * badges rather than as a second timeline, because the chain below already runs
 * through all of them in order and two orderings of the same run on one card is
 * one more than a reader can hold.
 */
function Stages({ stages }: { stages: Stage[] }): JSX.Element {
  return (
    <div className="ar-row" style={{ gap: 'var(--ar-space-2)', flexWrap: 'wrap' }}>
      {stages.map((stage) => {
        const status = STATUS_TONE[stage.status] ?? {
          tone: 'ar-badge--neutral',
          label: stage.status,
        }
        return (
          <span key={stage.id} className={`ar-badge ${status.tone}`}>
            {stage.label} · {status.label}
          </span>
        )
      })}
    </div>
  )
}

/**
 * One line of the chain.
 *
 * The spine's connecting line is omitted on the last step, which is what closes
 * the timeline — the prototype does the same, and a line running off the bottom
 * of the card reads as *"and then something we are not showing you"*.
 */
function TrailStep({
  step,
  timeZone,
  last,
}: {
  step: Step
  timeZone: string
  last: boolean
}): JSX.Element {
  const keys = Object.keys(step.detail)
  return (
    <div className="app-trail__item">
      <span className="app-trail__time">{formatTime(step.created_at, timeZone)}</span>
      <span className="app-trail__spine" aria-hidden="true">
        <span className="app-trail__dot" />
        {last ? null : <span className="app-trail__line" />}
      </span>
      <div className="app-trail__body">
        {/* The phase alone where the step named nothing. `title` is null in that
            case rather than falling back to the phase's own word, which the
            first build did and which rendered `PassExecutor`'s "Pass finished"
            as *"Acted — Acted"*. */}
        <strong>
          {step.phase_label}
          {step.title === null ? '' : ` — ${step.title}`}
        </strong>
        <span className="ar-muted" style={{ fontSize: 'var(--ar-text-body-sm-size)' }}>
          {step.body}
        </span>
        {step.model !== null ? (
          <span
            className="ar-subtle"
            style={{ display: 'block', fontSize: 'var(--ar-text-caption-size)' }}
          >
            {step.model}
            {step.input_tokens === null ? '' : ` · ${step.input_tokens.toLocaleString('en-IN')} in`}
            {step.output_tokens === null
              ? ''
              : ` · ${step.output_tokens.toLocaleString('en-IN')} out`}
          </span>
        ) : null}
        {keys.length > 0 ? (
          <details className="app-trail__detail">
            <summary>
              What this was based on ({keys.length} {keys.length === 1 ? 'field' : 'fields'})
            </summary>
            <pre className="app-trail__json">{JSON.stringify(step.detail, null, 2)}</pre>
          </details>
        ) : null}
      </div>
    </div>
  )
}
